/* ═══════════════════════════════════════════════════════════════════
   BLISS · PEACE — Yoga session audio.
   User requirement: NO tick sound during yoga. Only start & end cues.
   Aesthetic: single golden singing bowl. Zero-cost Web Audio synth.
   ─────────────────────────────────────────────────────────────────── */
import { getAudioCtx } from './audioCore';


let _ctx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  _ctx = getAudioCtx();
  return _ctx;
};

/** Play a warm sine partial — bowl-like resonant tone. */
const partial = ({ f, dur = 3.5, gain = 0.15, delay = 0, decay = 3.0 } = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(f, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.08);
  g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
  osc.connect(g).connect(ctx.destination);
  osc.start(t); osc.stop(t + dur);
};

/** SINGING BOWL — used to signal Start, Active→Breathing transition, and End.
 *  Layered partials produce a warm meditative gong that decays over ~4s. */
export const singingBowl = () => {
  // Fundamental + upper partials of a Tibetan singing bowl (~440Hz)
  partial({ f: 440, gain: 0.22, decay: 3.6 });
  partial({ f: 660, gain: 0.14, decay: 3.2, delay: 0.03 });
  partial({ f: 880, gain: 0.08, decay: 2.6, delay: 0.05 });
  partial({ f: 1320, gain: 0.04, decay: 1.8, delay: 0.08 });
};

/** DOUBLE BOWL — end-of-session flourish (two chimes ~1s apart). */
export const doubleBowl = () => {
  singingBowl();
  const ctx = getCtx();
  if (!ctx) return;
  setTimeout(() => singingBowl(), 1100);
};

/** Spoken cue via native speechSynthesis — used for phase announcements. */
export const speak = (text, { rate = 0.75, pitch = 0.9, volume = 0.85 } = {}) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  setTimeout(() => {          // off the UI frame — Android TTS can block
    try {
      const ss = window.speechSynthesis;
      if (ss.speaking || ss.pending) ss.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate; u.pitch = pitch; u.volume = volume;
      ss.speak(u);
    } catch { /* silent */ }
  }, 0);
};

export const stopSpeech = () => {
  try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
};

/* ═══════════════════════════════════════════════════════════════════
   AIR + VOID DRONE — continuous ambient background for yoga sessions.
   Layers a deep sine "void" tone with filtered white noise "air whoosh"
   that slowly pulses like breath. Starts on timer start, keeps playing
   through phase transitions, stops on end / stop / fuse.
   ═══════════════════════════════════════════════════════════════════ */

let _drone = null;

/* ══════════════════════════════════════════════════════════════════
   AIR DRONE — pure continuous wind, no void bass, no silence.
   Amplitude sways as a slow triangle: LOW → MEDIUM → HIGH → MEDIUM →
   LOW → repeat forever.  One 25-second cycle.
   ══════════════════════════════════════════════════════════════════ */
export const startAirVoidDrone = () => {
  const ctx = getCtx();
  if (!ctx) return;
  if (_drone) return;                 // already playing
  try { if (ctx.state === 'suspended') ctx.resume(); } catch { /* noop */ }

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, ctx.currentTime);
  master.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 1.6);
  master.connect(ctx.destination);

  /* AIR — pink-ish noise → highpass (kills sub-bass rumble = no ocean
     feel) → bandpass around ~800 Hz (breeze through leaves) → gentle
     high-shelf sparkle */
  const bufSec = 6;
  const buf = ctx.createBuffer(1, ctx.sampleRate * bufSec, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    last = last * 0.98 + (Math.random() * 2 - 1) * 0.02;
    data[i] = last;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf; noise.loop = true;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass'; hp.frequency.value = 220; hp.Q.value = 0.3;

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass'; bandpass.frequency.value = 800; bandpass.Q.value = 0.6;

  const hs = ctx.createBiquadFilter();
  hs.type = 'highshelf'; hs.frequency.value = 3000; hs.gain.value = 3.5;

  /* Amplitude LFO — slow triangle 1/25 Hz (25-second cycle).  Depth
     modulates the AIR gain between roughly 0.18 (low) and 0.85 (high).
     Because a triangle wave rises then falls, one full cycle traces:
       LOW → MEDIUM → HIGH → MEDIUM → LOW  — exactly per spec. */
  const airGain = ctx.createGain();
  airGain.gain.value = 0.5;                              // centre value
  const lfo = ctx.createOscillator();
  lfo.type = 'triangle';
  lfo.frequency.value = 1 / 25;                          // 25-s wind breath
  const lfoDepth = ctx.createGain();
  lfoDepth.gain.value = 0.34;                            // ±0.34 around 0.5

  noise.connect(hp).connect(bandpass).connect(hs).connect(airGain).connect(master);
  lfo.connect(lfoDepth).connect(airGain.gain);

  /* Slow filter drift — makes the wind feel "alive" instead of static
     hiss.  Second, faster LFO modulates the bandpass centre freq. */
  const filtLfo = ctx.createOscillator();
  filtLfo.type = 'sine';
  filtLfo.frequency.value = 1 / 7;                       // ~7-s breeze motion
  const filtDepth = ctx.createGain();
  filtDepth.gain.value = 260;                            // ±260 Hz around 800
  filtLfo.connect(filtDepth).connect(bandpass.frequency);

  noise.start(); lfo.start(); filtLfo.start();
  _drone = { ctx, master, nodes: [noise, lfo, filtLfo] };
};

export const stopAirVoidDrone = () => {
  if (!_drone) return;
  const { ctx, master, nodes } = _drone;
  _drone = null;
  try {
    const t = ctx.currentTime;
    master.gain.cancelScheduledValues(t);
    master.gain.setValueAtTime(master.gain.value, t);
    master.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
    setTimeout(() => {
      nodes.forEach((n) => { try { n.stop(); } catch { /* noop */ } });
      try { master.disconnect(); } catch { /* noop */ }
    }, 1400);
  } catch { /* noop */ }
};
